import { TreatmentCategory } from '../types';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

interface FormFieldProps {
  label: string;
  field: string;
  value: any;
  onChange: (field: string, value: any) => void;
  type?: 'text' | 'number' | 'textarea' | 'select';
  options?: string[];
}

interface TreatmentFormProps {
  category: TreatmentCategory;
  formData: Record<string, any>;
  onChange: (data: Record<string, any>) => void;
}

function FormField({
  label,
  field,
  value,
  onChange,
  type = 'text',
  options = [],
}: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={field}>{label}</Label>
      {type === 'select' ? (
        <select
          id={field}
          value={value || ''}
          onChange={(e) => onChange(field, e.target.value)}
          className="h-9 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-sm text-gray-900 outline-none transition-colors focus:border-[#E91E8C]"
        >
          <option value="" disabled>
            {`Seleccionar ${label.toLowerCase()}`}
          </option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <Textarea
          id={field}
          value={value || ''}
          onChange={(e) => onChange(field, e.target.value)}
          className="bg-gray-50 min-h-[100px]"
          placeholder={`Escribir ${label.toLowerCase()}...`}
        />
      ) : (
        <Input
          id={field}
          type={type}
          value={value || ''}
          onChange={(e) => onChange(field, e.target.value)}
          className="bg-gray-50"
          placeholder={`Escribir ${label.toLowerCase()}...`}
        />
      )}
    </div>
  );
}

export function TreatmentForm({ category, formData, onChange }: TreatmentFormProps) {
  const handleChange = (field: string, value: any) => {
    onChange({ ...formData, [field]: value });
  };

  const specialistOptions = ['Giovanna', 'Yolanda', 'Andrea', 'Tatiana'];

  const renderPestanasForm = () => (
    <>
      <FormField label="Tipo de Extensión" field="tipo" value={formData.tipo} onChange={handleChange} type="select" options={['Clásicas', 'Volumen', 'Volumen Ruso', 'Mega Volumen']} />
      <FormField label="Curva" field="curva" value={formData.curva} onChange={handleChange} type="select" options={['B', 'C', 'CC', 'D', 'L', 'M']} />
      <FormField label="Grosor" field="grosor" value={formData.grosor} onChange={handleChange} type="select" options={['0.03mm', '0.05mm', '0.07mm', '0.10mm', '0.12mm', '0.15mm']} />
      <FormField label="Longitudes" field="longitudes" value={formData.longitudes} onChange={handleChange} />
      <FormField label="Diseño" field="diseno" value={formData.diseno} onChange={handleChange} />
      <FormField label="Adhesivo Utilizado" field="adhesivo" value={formData.adhesivo} onChange={handleChange} />
      <FormField label="Observaciones" field="observaciones" value={formData.observaciones} onChange={handleChange} type="textarea" />
    </>
  );

  const renderLiftingForm = () => (
    <>
      <FormField label="Tipo de Molde" field="molde" value={formData.molde} onChange={handleChange} type="select" options={['S', 'M', 'L', 'XL']} />
      <FormField label="Producto Utilizado" field="producto" value={formData.producto} onChange={handleChange} />
      <FormField label="Tiempo de Exposición (minutos)" field="tiempo" value={formData.tiempo} onChange={handleChange} type="number" />
      <FormField label="Tinte Aplicado" field="tinte" value={formData.tinte} onChange={handleChange} />
      <FormField label="Observaciones" field="observaciones" value={formData.observaciones} onChange={handleChange} type="textarea" />
    </>
  );

  const renderCejasForm = () => (
    <>
      <FormField label="Tipo de Diseño" field="diseno" value={formData.diseno} onChange={handleChange} type="select" options={['Arqueadas', 'Rectas', 'Angulares', 'Redondas', 'S-Shape']} />
      <FormField label="Color Aplicado" field="color" value={formData.color} onChange={handleChange} />
      <FormField label="Técnica Utilizada" field="tecnica" value={formData.tecnica} onChange={handleChange} type="select" options={['Depilación con Cera', 'Depilación con Hilo', 'Tinte', 'Laminado']} />
      <FormField label="Observaciones" field="observaciones" value={formData.observaciones} onChange={handleChange} type="textarea" />
    </>
  );

  const renderMicropigmentacionForm = () => (
    <>
      <FormField label="Técnica" field="tecnica" value={formData.tecnica} onChange={handleChange} type="select" options={['Microblading', 'Powder Brows', 'Ombre', 'Combo', 'Labios Completos', 'Delineado']} />
      <FormField label="Pigmento Utilizado" field="pigmento" value={formData.pigmento} onChange={handleChange} />
      <FormField label="Aguja" field="aguja" value={formData.aguja} onChange={handleChange} />
      <FormField label="Profundidad" field="profundidad" value={formData.profundidad} onChange={handleChange} type="select" options={['Epidermis superficial', 'Epidermis media', 'Dermis superficial']} />
      <FormField label="Observaciones" field="observaciones" value={formData.observaciones} onChange={handleChange} type="textarea" />
      <FormField label="Advertencias y Cuidados" field="advertencias" value={formData.advertencias} onChange={handleChange} type="textarea" />
    </>
  );

  const renderManicuraPedicuraForm = () => (
    <>
      <FormField label="Tipo de Servicio" field="tipo" value={formData.tipo} onChange={handleChange} type="select" options={['Básica', 'Semipermanente', 'Acrílica', 'Polygel', 'Press-On']} />
      <FormField label="Color/Diseño" field="color" value={formData.color} onChange={handleChange} />
      <FormField label="Técnica Especial" field="tecnica" value={formData.tecnica} onChange={handleChange} type="select" options={['Ninguna', 'Francesa', 'Degradado', 'Nail Art', 'Cristales', 'Efecto Mate']} />
      <FormField label="Observaciones" field="observaciones" value={formData.observaciones} onChange={handleChange} type="textarea" />
    </>
  );

  const renderTratamientosFacialesForm = () => (
    <>
      <FormField label="Tipo de Tratamiento" field="tipo" value={formData.tipo} onChange={handleChange} type="select" options={['Limpieza Facial', 'Hidratación Profunda', 'Peeling', 'Microdermoabrasión', 'Radiofrecuencia', 'Mesoterapia']} />
      <FormField label="Productos Utilizados" field="productos" value={formData.productos} onChange={handleChange} type="textarea" />
      <FormField label="Duración (minutos)" field="duracion" value={formData.duracion} onChange={handleChange} type="number" />
      <FormField label="Observaciones" field="observaciones" value={formData.observaciones} onChange={handleChange} type="textarea" />
    </>
  );

  const renderTinteCabelloForm = () => (
    <>
      <FormField label="Tipo de Tinte" field="tipo" value={formData.tipo} onChange={handleChange} type="select" options={['Color Permanente', 'Semi-Permanente', 'Temporal', 'Mechas', 'Balayage', 'Ombré', 'Decoloración']} />
      <FormField label="Marca del Producto" field="marca" value={formData.marca} onChange={handleChange} />
      <FormField label="Color Aplicado" field="color" value={formData.color} onChange={handleChange} />
      <FormField label="Tonalidad Base/Natural" field="tonalidadBase" value={formData.tonalidadBase} onChange={handleChange} />
      <FormField label="Técnica Utilizada" field="tecnica" value={formData.tecnica} onChange={handleChange} type="select" options={['Raíces', 'Todo el Cabello', 'Mechas con Papel', 'Mechas con Gorro', 'A Mano Alzada (Balayage)', 'Degradado']} />
      <FormField label="Tiempo de Exposición (minutos)" field="tiempo" value={formData.tiempo} onChange={handleChange} type="number" />
      <FormField label="Volumen de Oxidante" field="oxidante" value={formData.oxidante} onChange={handleChange} />
      <FormField label="Observaciones" field="observaciones" value={formData.observaciones} onChange={handleChange} type="textarea" />
    </>
  );

  const renderCortePeloForm = () => (
    <>
      <FormField label="Tipo de Corte" field="tipo" value={formData.tipo} onChange={handleChange} type="select" options={['Mantenimiento', 'Corte Completo', 'Corte con Capas', 'Bob', 'Pixie', 'Largo a Corto', 'Flequillo', 'Desfilado']} />
      <FormField label="Largo Final" field="largo" value={formData.largo} onChange={handleChange} />
      <FormField label="Técnica Utilizada" field="tecnica" value={formData.tecnica} onChange={handleChange} type="select" options={['Tijera', 'Navaja', 'Tijera Entresacadora', 'Máquina', 'Mixta']} />
      <FormField label="Forma/Estilo" field="forma" value={formData.forma} onChange={handleChange} />
      <FormField label="Textura" field="textura" value={formData.textura} onChange={handleChange} type="select" options={['Lisa', 'Ondulada', 'Rizada', 'Desfilada', 'En Capas']} />
      <FormField label="Observaciones" field="observaciones" value={formData.observaciones} onChange={handleChange} type="textarea" />
    </>
  );

  return (
    <div className="space-y-5">
      <FormField label="Especialista" field="specialist" value={formData.specialist} onChange={handleChange} type="select" options={specialistOptions} />
      {category === 'pestanas' && renderPestanasForm()}
      {category === 'lifting' && renderLiftingForm()}
      {category === 'cejas' && renderCejasForm()}
      {(category === 'micropigmentacion-cejas' || category === 'micropigmentacion-labios' || category === 'micropigmentacion-ojos') && renderMicropigmentacionForm()}
      {(category === 'manicura' || category === 'pedicura') && renderManicuraPedicuraForm()}
      {category === 'tratamientos-faciales' && renderTratamientosFacialesForm()}
      {category === 'tinte-cabello' && renderTinteCabelloForm()}
      {category === 'corte-pelo' && renderCortePeloForm()}
    </div>
  );
}
